import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { ByzzerButton, ByzzerTipIcon } from '@byzzer/ui-components';
import { StoriesProvider } from '@/views/story/buildStory/StoriesContext';
import { useBetterNavigate } from '@/utils';
import { useLocation, useSearchParams } from 'react-router-dom';
import DashboardContent from '@/components/dashboard/DashboardContent';
import { StoryRunConifgWizard } from '../../views/story/buildStory/StoryRunConfigWizard';
import { useApp, useUser } from '@/contexts/UserContext';
import { storySelectorAttributes } from '@/utils/storySelectorUtil';
import { useStoryRunService } from '@/services/storiesRun.service';
import { CreditUsage } from '@/types/ApiTypes';
import { useTenantApi } from '@/hooks';
import { getAccessLevel } from '@/utils/storiesUtil';
export const StoryRunConfigEditor = () => {
    const wrapperClass = classnames('my-story');
    const navigate = useBetterNavigate();
    let { state, key }: any = useLocation();
    const [runValue, setRunValue] = useState((state?.storyConfig.reportDetails || state?.storyConfig.metadata)?.reportRunValue);
    const [reportType, setReportType] = useState<string>(state?.storyConfig?.metadata?.reportOptions?.reportType?.startsWith('smart') ? 'smart' : 'core');
    const [searchParams] = useSearchParams();
    const { allProducts } = useApp();
    const imageBucketURL = import.meta.env.REACT_APP_IMAGES_BASE_URL;
    const { getStoryDefaultRunConfigBySku } = useStoryRunService();
    const [loading, setLoading] = useState<boolean>(false);
    const {getMySubscriptionUsage, getStoryRunById} = useTenantApi();
    const [story, setStory] = useState(state);
    const {company} = useUser();
    const userAccessLevel = parseInt(getAccessLevel(company?.accessLevel) ?? '0');
    const editMode = searchParams.get('mode');
    const storyId = searchParams.get('id');
    
    useEffect(() => {
        if (!story) {
           editMode && storyId ? loadStoryDetails() : loadConfig();
            // navigate('/dashboard/stories/library');
        } else {
            // copied it here from Stories Library page so logic doesn't break
            localStorage.setItem('storySku', story?.storyConfig?.sku ?? '');
            localStorage.setItem('dataSpecificationValues', '{}');
            localStorage.removeItem('selectorsConfigurations');
            localStorage.removeItem('selectedObjectives');
        }
    }, []);

    const loadConfig = async () => {
        if (allProducts) {
            try {
                setLoading(true);
                const product = allProducts.filter((i) => i.sku === searchParams.get('sku'))?.[0];
                if (!product?.thumbnailUrl?.includes(imageBucketURL, 0)) {
                    product.thumbnailUrl = imageBucketURL + product?.thumbnailUrl;
                }

                const reportDetails = {
                    reportType: product?.type,
                    reportSelectors: product.metadata?.env?.reportSelectors
                        ? storySelectorAttributes(product?.metadata?.env?.reportSelectors)
                        : {},
                    productSku: product?.sku,
                    storyType: product.metadata?.env?.storyType,
                    reportRunValue: product.metadata?.env?.reportRunValue,
                };
                const storyConfig = { ...product, reportDetails };
                if (userAccessLevel === 5) {
                    storyConfig.metadata.env.reportSelectors = storyConfig.metadata?.env?.reportSelectors.filter(
                        (e) => e !== 'market:option5'
                    );
                }
                const credits = await getMySubscriptionUsage();
                let config = {
                    storyConfig,
                    defaultValues: getStoryDefaultRunConfigBySku(product?.sku),
                    remainingReportRuns: credits?.basicReports.limit - credits?.basicReports.used,
                };
                setStory(config);
                setRunValue((config?.storyConfig.reportDetails || config?.storyConfig.metadata)?.reportRunValue);
                setReportType(
                    config?.storyConfig?.metadata?.reportOptions?.reportType?.startsWith('smart') ? 'smart' : 'core'
                );
                // copied it here from Stories Library page so logic doesn't break
                localStorage.setItem('storySku', storyConfig?.sku ?? '');
                localStorage.setItem('dataSpecificationValues', '{}');
                localStorage.removeItem('selectorsConfigurations');
                localStorage.removeItem('selectedObjectives');
            } finally {
                setLoading(false);
            }
        }
    };

    const loadStoryDetails = async () => {
        let { configuration, sku, story } = await getStoryRunById(Number(storyId));
        const usage = await getMySubscriptionUsage();
        setStory({
            storyConfig: { ...story },
            defaultValues: { ...configuration.runConfig },
            remainingReportRuns: usage?.basicReports ? usage.basicReports.limit - usage.basicReports.used : 0,
        });

        setRunValue((story?.reportDetails || story.metadata.env)?.reportRunValue);
        setReportType(story?.storyConfig?.metadata?.reportOptions?.reportType?.startsWith('smart') ? 'smart' : 'core');
    };

    return (
        <StoriesProvider>
            <DashboardContent
                title={story?.storyConfig.title}
                numberTag={<NumberTagReportRuns value={runValue} reportRuns={story?.remainingReportRuns} reportType={reportType} />}
                extras={
                    <div className="byzzer-btn">
                        <ByzzerButton
                            label={'Cancel'}
                            onClick={key !== "default" ? () => navigate(-1): ()=> navigate('/dashboard/stories/library')}
                            disabled={undefined}
                        />
                    </div>
                }
                className={classnames(wrapperClass)}
                key={story?.sku}
                links = {undefined} // have to assign a default value, otherwise it will be 'true'
                exact = {undefined}
                replace = {undefined}
                loading={loading && !story}
            >
                <StoryRunConifgWizard state={story} />
            </DashboardContent>
        </StoriesProvider>
    );
};

function NumberTagReportRuns({ value, reportRuns, reportType }) {

    if(reportRuns === Infinity) return <></>;

    return (
        value ? <div className={`report-run-selector__tag`}>
            {value > 1 ? `-${value} runs` : `-${value} run`}
            <ByzzerTipIcon
                tip={`This will deduct ${
                    value > 1 ? `${value} ${reportType} run credits` : `${value} ${reportType} run credit`
                } from your subscription`}
                tipLocation="right"
            />
        </div> : <></>
    );
}
